import { TextField, SelectField, TextAreaField } from '../../atomic/forms';
import {
    NotificationRequest,
    notificationsAPI,
} from '../../../APIs/notificationsAPI';
import React, { useMemo } from 'react';
import { mutateAsync, useGet } from '../../../hooks/useApi';
import { Loading } from '../../atomic/effects';
import { CheckBox } from '../../atomic/switches';
import { Button } from '@aws-amplify/ui-react';
import { useNavigate, useParams } from 'react-router';
import { NotificationView } from '../../notification/notificationShow';
import { Icon, Icons } from '../../atomic/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { TenantModel } from '../../../models/TenantModel';
import { getTenantsForSuperAdmin } from '../../../APIs/tenants';
import { SimpleDialogProps, useDialog } from '../../../hooks/useDialog';
import { DialogBox } from '../../atomic/dialog';
import { NotificationType } from '../../../models/NotificationModel';
import { NotificationTypeLabels } from '../../notification/NotificationLabel';

const BackDialog = (opts: SimpleDialogProps) => (
    <DialogBox title="確認" {...opts}>
        編集内容は更新されません
        <br />
        一覧に戻りますか？
    </DialogBox>
);

const typeOptions = Object.entries(NotificationTypeLabels).map(
    ([value, text]) => ({ value, text })
);

export const NotificationEdit = () => {
    const [notification, setNotification] =
        React.useState<NotificationRequest>();
    const { isSuperAdmin } = useAuth();

    const [preview, setPreview] = React.useState(false);
    const { notificationId } = useParams();
    const navigate = useNavigate();
    const { data } = useGet(
        notificationId ? notificationsAPI.admin.getSingle(notificationId) : null
    );
    const [isLoading, setIsLoading] = React.useState(false);
    const [message, setMessage] = React.useState<string>('');
    const [tenants, setTenants] = React.useState<
        Array<TenantModel> | undefined
    >(undefined);

    const backDialog = useDialog(
        BackDialog,
        () => ({
            options: {},
            onCommit: async () => navigate(`/app/${authMode}/notifications`),
        }),
        [navigate]
    );

    React.useEffect(() => {
        if (!notificationId) {
            setNotification({
                notificationType: NotificationType.info,
                tenantId: '',
                title: '',
                content: '',
                isVisibleLogin: true,
                isVisible: false,
            });
        }
        if (data) {
            setNotification(data.data);
        }
    }, [notificationId, data]);

    const tenantOptions = useMemo(() => {
        if (!tenants) {
            return [];
        }
        const options = tenants.map(({ tenantId, name }) => ({
            text: name,
            value: tenantId,
        }));

        return [{ text: '全てのテナント', value: '' }, ...options];
    }, [tenants]);

    React.useEffect(() => {
        if (isSuperAdmin()) {
            getTenantsForSuperAdmin()
                .then((resp) => {
                    setTenants(resp.data);
                })
                .catch((err) => {
                    console.warn(err);
                });
        }
    }, []);

    const authMode = isSuperAdmin() ? 'superadmin' : 'admin';
    const setContent = (
        key:
            | 'notificationType'
            | 'tenantId'
            | 'content'
            | 'title'
            | 'isVisibleLogin'
            | 'isVisible',
        value: string | number | boolean
    ) => {
        if (!notification) {
            return;
        }
        const _notification = { ...notification, [key]: value };
        setNotification(_notification);
    };
    const onOK = React.useCallback(async () => {
        if (notification) {
            try {
                setIsLoading(true);
                const result = await mutateAsync(
                    notificationsAPI.admin.mutate,
                    notification
                ).finally(() => {
                    setIsLoading(false);
                });

                if (result.status === 200) {
                    setMessage('お知らせを更新しました');
                }
                navigate(`/app/${authMode}/notifications`);
            } catch (err: any) {
                setMessage(err.message);
            }
        }
    }, [notification]);

    if (!notification) {
        return <Loading />;
    }

    return (
        <>
            {isLoading && <Loading />}
            <div className="app-main admin-info-edit home">
                <div className="header">
                    <h2>
                        {notificationId ? 'お知らせの編集' : 'お知らせ作成'}
                        <span
                            className={'preview-switch'}
                            onClick={() => setPreview((prev) => !prev)}
                        >
                            <Icon icon={Icons.RightLeft} />
                            {preview ? '編集' : 'プレビュー'}
                        </span>
                    </h2>
                    {preview ? (
                        <NotificationView notification={notification} />
                    ) : (
                        <>
                            <div className="error">{message}</div>
                            <ul className="fields">
                                <li>
                                    <SelectField
                                        label="タイプ"
                                        value={
                                            notification.notificationType as string
                                        }
                                        _onChange={(value: string) => {
                                            setContent(
                                                'notificationType',
                                                value
                                            );
                                        }}
                                        options={typeOptions}
                                        disabled={notification.isVisible}
                                    />
                                </li>
                                {isSuperAdmin() && (
                                    <li>
                                        <SelectField
                                            label="テナント"
                                            value={notification.tenantId}
                                            _onChange={(value: string) => {
                                                setContent('tenantId', value);
                                            }}
                                            options={tenantOptions}
                                            disabled={notification.isVisible}
                                        />
                                    </li>
                                )}
                                <li>
                                    <TextField
                                        label="タイトル"
                                        value={notification.title}
                                        _onChange={(value) => {
                                            setContent('title', value);
                                        }}
                                        placeholder={'タイトル'}
                                        disabled={notification.isVisible}
                                    />
                                    {notification.title.length === 0 && (
                                        <div className="color-error-text">
                                            {'タイトルは必須項目です'}
                                        </div>
                                    )}
                                </li>
                                <li>
                                    <TextAreaField
                                        rows={8}
                                        className="textfield"
                                        label="詳細"
                                        value={notification.content}
                                        onChange={(e) => {
                                            setContent(
                                                'content',
                                                e.target.value
                                            );
                                        }}
                                        disabled={notification.isVisible}
                                    />
                                    {isSuperAdmin() &&
                                        !notification.tenantId && (
                                            <li>
                                                <CheckBox
                                                    checked={
                                                        notification.isVisibleLogin
                                                            ? true
                                                            : false
                                                    }
                                                    name={
                                                        'notification.isVisibleLogin'
                                                    }
                                                    label="ログイン画面に表示"
                                                    _onChange={(
                                                        checked: boolean
                                                    ) => {
                                                        setContent(
                                                            'isVisibleLogin',
                                                            !notification.isVisibleLogin
                                                        );
                                                    }}
                                                    disabled={
                                                        notification.isVisible
                                                    }
                                                />
                                            </li>
                                        )}
                                </li>
                                {
                                    <li>
                                        <CheckBox
                                            checked={
                                                notification.isVisible
                                                    ? true
                                                    : false
                                            }
                                            name={'notification.isVisible'}
                                            label="表示する"
                                            _onChange={(checked: boolean) => {
                                                setContent(
                                                    'isVisible',
                                                    !notification.isVisible
                                                );
                                            }}
                                        />
                                    </li>
                                }
                            </ul>
                        </>
                    )}
                    {preview ? (
                        <>
                            <Button
                                onClick={onOK}
                                className="atomic button primary"
                            >
                                {notificationId ? '更新' : '作成'}
                            </Button>
                            <Button
                                onClick={() => setPreview((prev) => !prev)}
                                className="atomic button secondary"
                            >
                                編集に戻る
                            </Button>
                        </>
                    ) : (
                        <Button
                            onClick={() => setPreview((prev) => !prev)}
                            className="atomic button primary"
                            disabled={notification.title.length === 0}
                        >
                            確認
                        </Button>
                    )}
                    <Button
                        className="atomic button secondary"
                        onClick={() => {
                            backDialog.show();
                        }}
                    >
                        一覧へ戻る
                    </Button>
                    {backDialog.dialog}
                </div>
            </div>
        </>
    );
};
